<ng-container *transloco="let t; read: 'expenses'">
  <!-- Fixed desktop width, capped to the viewport on phones; the content area scrolls within maxHeight. -->
  <kendo-dialog [title]="t(dialogTitle)" (close)="cancel()" [width]="700" [maxWidth]="'95vw'" [maxHeight]="'90vh'">
    <kendo-tabstrip>
      <kendo-tabstrip-tab [title]="t('detailsTab')" [selected]="true">
        <ng-template kendoTabContent>

          <!-- edit / create -->
          <form *ngIf="editable" [formGroup]="formGroup" class="flex-col mbm">
            <kendo-label [text]="t('date')"></kendo-label>
            <kendo-datepicker formControlName="date" format="dd.MM.yyyy"></kendo-datepicker>

            <kendo-label [text]="t('expenseType')"></kendo-label>
            <kendo-dropdownlist
              [data]="expenseTypes"
              textField="name"
              valueField="id"
              [valuePrimitive]="true"
              [defaultItem]="{ id: null, name: t('selectExpenseType') }"
              formControlName="expenseTypeId">
            </kendo-dropdownlist>

            <ng-container *ngIf="showKm">
              <kendo-label [text]="t('km')"></kendo-label>
              <kendo-numerictextbox formControlName="km" [decimals]="1" [format]="'n1'" [min]="0"></kendo-numerictextbox>
            </ng-container>

            <ng-container *ngIf="showAmount">
              <kendo-label [text]="t('amount')"></kendo-label>
              <kendo-numerictextbox formControlName="amount" [decimals]="2" [format]="'n2'" [min]="0"></kendo-numerictextbox>
            </ng-container>

            <kendo-label [text]="t('description')"></kendo-label>
            <kendo-textarea formControlName="description" [rows]="3" resizable="vertical"></kendo-textarea>

            <kendo-label [text]="t('assignment')"></kendo-label>
            <kendo-combobox
              [data]="assignments"
              textField="title"
              valueField="id"
              [valuePrimitive]="true"
              [filterable]="true"
              [loading]="assignmentsLoading"
              [placeholder]="t('searchAssignment')"
              (filterChange)="onAssignmentFilter($event)"
              formControlName="linkedAssignmentId">
            </kendo-combobox>

            <div *ngIf="expense" class="mts">
              <strong>{{ t('status') }}:</strong>
              <span class="status-badge" [ngClass]="statusClass(expense.status)">
                {{ t('expenseStates.' + statusName(expense.status)) }}
              </span>
            </div>
          </form>

          <!-- read-only view -->
          <div *ngIf="!editable && expense" class="flex-col mbm">
            <table>
              <tr>
                <td><strong>{{ t('date') }}:</strong></td>
                <td>{{ expense.date | date:'dd.MM.yyyy' }}</td>
              </tr>
              <tr>
                <td><strong>{{ t('expenseType') }}:</strong></td>
                <td>{{ expense.expenseType?.name }}</td>
              </tr>
              <tr>
                <td><strong>{{ t('amount') }}:</strong></td>
                <td>CHF {{ expense.amount | number:'1.2-2' }}</td>
              </tr>
              <tr *ngIf="expense.km != null">
                <td><strong>{{ t('km') }}:</strong></td>
                <td>{{ expense.km | number:'1.1-1' }}</td>
              </tr>
              <tr>
                <td><strong>{{ t('status') }}:</strong></td>
                <td>
                  <span class="status-badge" [ngClass]="statusClass(expense.status)">
                    {{ t('expenseStates.' + statusName(expense.status)) }}
                  </span>
                </td>
              </tr>
              <tr *ngIf="expense.description">
                <td><strong>{{ t('description') }}:</strong></td>
                <td>{{ expense.description }}</td>
              </tr>
              <tr *ngIf="expense.linkedAssignmentTitle">
                <td><strong>{{ t('assignment') }}:</strong></td>
                <td>{{ expense.linkedAssignmentTitle }}</td>
              </tr>
            </table>
          </div>

          <app-attachments *ngIf="currentId && requiresReceipt" [expenseId]="currentId" [uploadTitle]="t('uploadReceipts')" [readonly]="!editable"></app-attachments>
        </ng-template>
      </kendo-tabstrip-tab>

      <kendo-tabstrip-tab [title]="t('historyTab')" *ngIf="expense">
        <ng-template kendoTabContent>
          <kendo-grid [kendoGridBinding]="expense.expenseHistory ?? []">
            <kendo-grid-column field="status" [title]="t('status')">
              <ng-template kendoGridCellTemplate let-dataItem>
                {{ t('expenseStates.' + statusName(dataItem.status)) }}
              </ng-template>
            </kendo-grid-column>
            <kendo-grid-column field="decisionDate" [title]="t('decisionDate')">
              <ng-template kendoGridCellTemplate let-dataItem>
                {{ (dataItem.decisionDate || dataItem.createdOn) | date:'dd.MM.yyyy HH:mm' }}
              </ng-template>
            </kendo-grid-column>
            <kendo-grid-column field="decisionMakerId" [title]="t('decisionMaker')">
              <ng-template kendoGridCellTemplate let-dataItem>
                {{ (dataItem.decisionMakerId || expense.submitterId) | employee | async }}
              </ng-template>
            </kendo-grid-column>
            <kendo-grid-column field="rejectionReason" [title]="t('rejectionReason')"></kendo-grid-column>
          </kendo-grid>
        </ng-template>
      </kendo-tabstrip-tab>
    </kendo-tabstrip>

    <kendo-dialog-actions>
      <button kendoButton (click)="cancel()">{{ editable ? t('cancel') : t('close') }}</button>
      <button *ngIf="editable" kendoButton themeColor="primary" [disabled]="formGroup.invalid" (click)="save()">
        {{ t('save') }}
      </button>
      <button *ngIf="editable" kendoButton themeColor="success" [disabled]="!currentId" (click)="submit()">
        {{ t('submit') }}
      </button>

      <!-- reviewer actions on a still-submitted expense -->
      <ng-container *ngIf="canDecide">
        <button kendoButton themeColor="success" (click)="acceptExpense()">
          {{ t('approve') }}
        </button>
        <button kendoButton themeColor="error" (click)="openReject()">
          {{ t('reject') }}
        </button>
      </ng-container>

      <!-- reviewer actions on an already-processed expense -->
      <ng-container *ngIf="canReprocess">
        <button *ngIf="expense.status === ExpenseStatus.Rejected" kendoButton themeColor="success" (click)="acceptExpense()">
          {{ t('approve') }}
        </button>
        <button *ngIf="expense.status === ExpenseStatus.Accepted" kendoButton themeColor="error" (click)="openReject()">
          {{ t('reject') }}
        </button>
      </ng-container>
    </kendo-dialog-actions>
  </kendo-dialog>

  <app-expense-reason-dialog
    *ngIf="reasonOpen"
    titleKey="reject"
    confirmKey="reject"
    (confirm)="onReasonConfirm($event)"
    (cancel)="closeReason()">
  </app-expense-reason-dialog>
</ng-container>
